import { useRef, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "context/LanguageContext";

// theme="dark"  → white text/border (for the dark green topbar)
// theme="light" → dark text/border (for the white main header bar)
export function LanguageDropdown({ theme = "dark", className = "" }) {
  const { language, setLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const options = ["EN", "ML"];
  const isDark = theme === "dark";
  const btnBorderColor = isDark ? "rgba(255,255,255,0.45)" : "rgba(0,58,55,0.35)";
  const btnTextColor   = isDark ? "#ffffff" : "#003a37";
  const marginLeft     = isDark ? "16px" : "8px";

  return (
    <div ref={ref} style={{ position: "relative", marginLeft }} className={className}>
      {/* Trigger button */}
      <button
        onClick={() => setOpen((prev) => !prev)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          padding: "4px 10px",
          background: "transparent",
          border: `1.5px solid ${btnBorderColor}`,
          borderRadius: "4px",
          color: btnTextColor,
          fontSize: "13px",
          fontWeight: "600",
          cursor: "pointer",
          letterSpacing: "0.4px",
          whiteSpace: "nowrap",
        }}
      >
        {language}
        <i
          className={`ri-arrow-${open ? "up" : "down"}-s-line`}
          style={{ fontSize: "16px", lineHeight: 1 }}
        />
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            minWidth: "80px",
            background: "#ffffff",
            border: "1.5px solid #e0e0e0",
            borderRadius: "4px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
            zIndex: 9999,
            overflow: "hidden",
          }}
        >
          {options.map((lang) => (
            <div
              key={lang}
              onClick={() => {
                setLanguage(lang);
                setOpen(false);
              }}
              style={{
                padding: "9px 18px",
                fontSize: "13px",
                fontWeight: language === lang ? "700" : "500",
                color: language === lang ? "#25826a" : "#333333",
                background: language === lang ? "#f0faf7" : "transparent",
                cursor: "pointer",
                borderLeft: language === lang ? "3px solid #25826a" : "3px solid transparent",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => {
                if (language !== lang) e.currentTarget.style.background = "#f5f5f5";
              }}
              onMouseLeave={(e) => {
                if (language !== lang) e.currentTarget.style.background = "transparent";
              }}
            >
              {lang}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Header({ onToggle }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const mainBoxRef = useRef(null);
  const protectedRoutes = ["/userDashboard", "/claimLoaneeIdentification", "/loanDetails", "/diseasesClaim", "/deathClaim", "/previewDiseaseClaim", "/previewDeathClaim"];
  const isLoggedIn = !!sessionStorage.getItem("jwt") && protectedRoutes.includes(location.pathname);
  const userName = sessionStorage.getItem("userName") || "";

  const handleLogout = () => {
    sessionStorage.removeItem("jwt");
    sessionStorage.removeItem("userName");
    navigate("/");
  };

  const handleNavClick = (e, sectionId) => {
    e.preventDefault();
    if (sectionId) {
      if (location.pathname === "/") {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate("/", { state: { scrollTo: sectionId } });
      }
    } else {
      if (location.pathname === "/") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        navigate("/");
      }
    }
  };

  // Sticky header on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (mainBoxRef.current) {
        if (window.scrollY > 100) {
          mainBoxRef.current.classList.add("sticky");
        } else {
          mainBoxRef.current.classList.remove("sticky");
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header className="rs-header" id="rs-header">
      <div className="rs-header__topbar">
        <div className="container-fluid">
          <div className="row">
            {/* <div className="col-lg-7">
              <div className="rs-header__info">
                <ul>
                  <li>
                    <div className="icon">
                      <i className="ri-map-pin-2-line"></i>
                    </div>{" "}
                    {t.address}
                  </li>
                </ul>
              </div>
            </div> */}
            <div className="col-lg-12">
              <div className="rs-header__top-rightbar">
                <div className="rs-header__info item-2">
                  <ul>
                    <li>
                      <a href="mailto:info@rwfms.com">
                        <div className="icon">
                          <i className="ri-mail-send-line"></i>
                        </div>{" "}
                        info@rwfms.com
                      </a>
                    </li>
                  </ul>
                </div>

                {/* Dark-themed dropdown for the dark green topbar */}
                <LanguageDropdown theme="dark" />

                <div className="rs-header__social">
                  <ul>
                    <li>
                      <a href="#">
                        <i className="ri-facebook-fill"></i>
                      </a>
                    </li>
                    <li>
                      <a href="#">
                        <i className="ri-instagram-line"></i>
                      </a>
                    </li>
                    <li>
                      <a href="#">
                        <i className="ri-youtube-fill"></i>
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="rs-header__main-box" ref={mainBoxRef}>
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="rs-header__main">
                <div className="rs-logo-area">
                  <a href="#">
                    <img src={`${process.env.PUBLIC_URL}/assets/images/LOGO-RWFMS.png`} alt="" />
                  </a>
                </div>
                <div className="rs-header-rightside">
                  {/* Desktop nav — visible only at lg (≥992px) */}
                  <div className="main-menu d-none d-lg-flex">
                    <ul className="nav-menu">
                      <li>
                        <a href="/" onClick={(e) => handleNavClick(e, null)}>{t.home}</a>
                      </li>
                      <li>
                        <a href="/" onClick={(e) => handleNavClick(e, "rs-about")}>{t.about}</a>
                      </li>
                      <li>
                        <a href="/" onClick={(e) => handleNavClick(e, "rs-scheme")}>{t.schemes}</a>
                      </li>
                      <li>
                        <a href="/" onClick={(e) => handleNavClick(e, "rs-process")}>{t.howItWorks}</a>
                      </li>
                      {isLoggedIn && (
                        <li>
                          <a href="#" onClick={(e) => { e.preventDefault(); handleLogout(); }}>Logout</a>
                        </li>
                      )}
                    </ul>
                    {isLoggedIn && (
                      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginLeft: "20px", flexShrink: 0 }}>
                        <div style={{
                          width: "44px", height: "44px", borderRadius: "50%",
                          border: "2px solid #25826a", display: "flex", flexShrink: 0,
                          alignItems: "center", justifyContent: "center", background: "#f0faf7"
                        }}>
                          <i className="ri-user-line" style={{ fontSize: "20px", color: "#25826a" }}></i>
                        </div>
                        <div style={{ lineHeight: "1.3", whiteSpace: "nowrap" }}>
                          <div style={{ fontWeight: "700", fontSize: "15px", color: "#003a37" }}>{userName}</div>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Login button — only on landing page */}  
                                                                                             
                        {location.pathname === "/" && (                                                                                         
                         <div className="rs-header__nav-btn"> 
                         {/* onClick={() => navigate("/login")}                                                                                  */}
                          <button className="btn main-btn">                                                
                             <i className="ri-login-box-line"></i>                                                                             
                           <span className="rs-login-text">{t.login}</span>                                                                  
                         </button>                                                                                                           
                         </div>                                                                                                                
                    )}         
                  {/* Light-themed dropdown in the white header bar — only on mobile (topbar is hidden) */}
                  <LanguageDropdown theme="light" className="d-md-none" />
                  {/* Hamburger — visible on mobile + tablet (< 992px) */}
                  <div
                    className="rs-header-toggle off-canver-menu d-lg-none"
                    onClick={onToggle}
                  >
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
